import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getTrashedRequests,
  purgeTrashedRequests,
  restoreRequest,
} from '../../../shared/api/requests.api'
import { PageLoader } from '../../../shared/ui/Spinner'
import { EmptyState } from '../../../shared/ui/EmptyState'
import { toast } from '../../../shared/ui/toastStore'
import { confirm } from '../../../shared/ui/confirmStore'
import { STATUS_LABELS } from './requestsColDefs'

function extractError(e: unknown): string {
  const err = e as { response?: { data?: { error?: { message?: string } } } }
  return err.response?.data?.error?.message ?? 'Не удалось выполнить действие'
}

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })

/**
 * «Корзина» заявок: мягко удалённые заявки с восстановлением. Хранение 30 дней,
 * дальше авто-очистка (окончательное каскадное удаление). Восстанавливать могут
 * ADMIN/MANAGER/ROP (ownership на бэке); ГД — только просмотр.
 *
 * «Удалить окончательно» (18.09.2026) — только ADMIN: без него клиента с
 * удалённой заявкой нельзя было удалить, пока корзина не очистится сама.
 */
export function RequestsTrashModal({
  canRestore,
  canPurge,
  onClose,
}: {
  canRestore: boolean
  /** Окончательное удаление — только ADMIN (на сервере роут отдаёт 403). */
  canPurge: boolean
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [restoringId, setRestoringId] = useState<string | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [purging, setPurging] = useState(false)

  const { data: rows, isLoading } = useQuery({
    queryKey: ['requests-trash'],
    queryFn: getTrashedRequests,
  })

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const allSelected = !!rows && rows.length > 0 && selected.size === rows.length
  const toggleAll = () =>
    setSelected(allSelected || !rows ? new Set() : new Set(rows.map((r) => r.id)))

  const handleRestore = async (id: string, requestNumber: string | null) => {
    setRestoringId(id)
    try {
      await restoreRequest(id)
      toast.success(`Заявка ${requestNumber ?? ''} восстановлена`.trim())
      setSelected((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['requests-trash'] }),
        queryClient.invalidateQueries({ queryKey: ['requests'] }),
      ])
    } catch (e) {
      toast.error(extractError(e))
    } finally {
      setRestoringId(null)
    }
  }

  const handlePurge = async () => {
    const ids = [...selected]
    if (ids.length === 0) return
    const ok = await confirm({
      title: 'Удалить окончательно?',
      message: `Заявки удалятся без возможности восстановления: ${ids.length} шт. Продолжить?`,
      confirmText: 'Удалить окончательно',
      danger: true,
    })
    if (!ok) return
    setPurging(true)
    try {
      const res = await purgeTrashedRequests(ids)
      const skipped = res.skippedWithOrder.length
      toast.success(
        skipped > 0
          ? `Удалено ${res.purged}, пропущено ${skipped} (с оформленным заказом)`
          : `Удалено ${res.purged}`,
      )
      setSelected(new Set())
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['requests-trash'] }),
        queryClient.invalidateQueries({ queryKey: ['clients'] }),
      ])
    } catch (e) {
      toast.error(extractError(e))
    } finally {
      setPurging(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="bg-white rounded-xl border border-gray-200 shadow-xl w-full max-w-3xl mx-4 max-h-[80vh] flex flex-col">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800">
            Корзина заявок
            {rows && rows.length > 0 && (
              <span className="ml-2 text-gray-400 font-normal">({rows.length})</span>
            )}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" title="Закрыть">
            ✕
          </button>
        </div>
        <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-50">
          Удалённые заявки хранятся 30 дней, затем стираются безвозвратно вместе с позициями,
          квотами и КП.
        </div>
        {canPurge && rows && rows.length > 0 && (
          <div className="px-4 py-2 border-b border-gray-100 flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                className="rounded border-gray-300"
              />
              Выбрать все
            </label>
            <span className="text-xs text-gray-400">Выбрано: {selected.size}</span>
            <button
              onClick={() => void handlePurge()}
              disabled={selected.size === 0 || purging}
              className="ml-auto px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {purging ? 'Удаление…' : 'Удалить окончательно'}
            </button>
          </div>
        )}
        <div className="overflow-y-auto flex-1">
          {isLoading ? (
            <PageLoader />
          ) : !rows || rows.length === 0 ? (
            <EmptyState title="Корзина пуста" />
          ) : (
            <ul className="divide-y divide-gray-100">
              {rows.map((r) => (
                <li key={r.id} className="px-4 py-3 flex items-center gap-3 flex-wrap">
                  {canPurge && (
                    <input
                      type="checkbox"
                      checked={selected.has(r.id)}
                      onChange={() => toggle(r.id)}
                      className="rounded border-gray-300"
                      aria-label={`Выбрать заявку ${r.requestNumber ?? ''}`}
                    />
                  )}
                  <div className="flex-1 min-w-[220px]">
                    <div className="text-sm text-gray-900">
                      <span className="font-medium">{r.requestNumber ?? 'Без номера'}</span>
                      <span className="mx-1.5 text-gray-300">·</span>
                      {r.client?.name ?? '—'}
                      <span className="ml-2 text-xs text-gray-400">
                        {(STATUS_LABELS as Record<string, string>)[r.status] ?? r.status}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Позиций: {r._count.items} · КП: {r._count.quotationSnapshots} · Менеджер:{' '}
                      {r.manager?.name ?? r.manager?.email ?? '—'}
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      Удалена {fmtDate(r.deletedAt)}
                      {r.deletedBy ? ` — ${r.deletedBy.name ?? r.deletedBy.email}` : ''}
                    </div>
                  </div>
                  {canRestore && (
                    <button
                      onClick={() => void handleRestore(r.id, r.requestNumber)}
                      disabled={restoringId !== null || purging}
                      className="px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50 whitespace-nowrap"
                    >
                      {restoringId === r.id ? 'Восстановление…' : 'Восстановить'}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}
